import { ProductAnalyticsTag } from '../common/Constants';
import Log from '../common/log';
import Constants from '../analytics/constants';
import UUIDGenerator from '../analytics/deviceUUID/hashgenerator';
import Core from '../core/Core';
import ProductAnalyticsSettings from './ProductAnalyticsSettings';
import ProductAnalyticsConfig from './ProductAnalyticsConfig';
import ProductAnalyticsEventType from './ProductAnalyticsEventTypes';
import Experiment from './experiments/Experiment';
/**
 * NpawProductAnalytics class is the base of npaw productAnalytics.
 * Every plugin will have an instance.
 */
export default class ProductAnalytics {
    /**
     * Constructs the NpawProductAnalytics class
     */
    constructor(session, getReferral, setAnalyticsOptions, addVideoRequestListener, removeVideoRequestListener) {
        this.session = session;
        this.session.addListenerRequestQueued(this.onRequestQueued.bind(this));
        this.session.addListenerRequestWillSend(this.onRequestWillSend.bind(this));
        this.session.addListenerRequestSent(this.onRequestSent.bind(this));
        this.initialized = false;
        this.settings = new ProductAnalyticsSettings();
        this.page = null;
        this.config = null;
        this.searchQuery = '';
        this.url = null;
        if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
            try {
                this.url = new URL(window.location.href);
            }
            catch (ex) {
                this.url = null;
            }
        }
        this.contentHighlighted = null;
        this.contentHighlightTimeout = null;
        this.pendingVideoEvents = {};
        this.adapters = {};
        this.getReferral = getReferral;
        this.setAnalyticsOptions = setAnalyticsOptions;
        this.addVideoRequestListener = addVideoRequestListener;
        this.removeVideoRequestListener = removeVideoRequestListener;
        this.experiments = new Experiment(Core.getInstance().getFastDataService().getAccountCode(), this.getUserId(), this.getProfileId(), this.getDeviceUUID());
        /*
        We have to create a bound accessor to allow this.removeVideoRequestListener recognize and
        remove the listener successfully. Calling removeVideoRequestListener supplying
        this.videoListener.bind(this) as an argument does not work.
        */
        this.videoListenerAccessor = this.videoListener.bind(this);
    }
    /**
     * Initializes product analytics
     * @param {Object} productAnalyticsSettings product analytics settings
     * @param {Object} [npawtmConfigValue] configuration settings
     */
    initialize(productAnalyticsSettings) {
        let query;
        // Load settings
        if (typeof productAnalyticsSettings === 'undefined') {
            this.settings = new ProductAnalyticsSettings();
            Log.warn(ProductAnalyticsTag, 'Settings not supplied: using default ones.');
        }
        else if (productAnalyticsSettings instanceof ProductAnalyticsSettings) {
            this.settings = productAnalyticsSettings;
        }
        else {
            const settings = ProductAnalyticsSettings.buildFromObject(productAnalyticsSettings);
            if (settings === null) {
                this.settings = new ProductAnalyticsSettings();
                Log.warn(ProductAnalyticsTag, 'Invalid settings supplied: using default ones.');
            }
            else {
                this.settings = settings;
            }
        }
        // Validate settings
        this.settings.validate();
        // Identify current page
        this.config = new ProductAnalyticsConfig(this.fireEventInternal.bind(this));
        this.identify();
        // Set as initialized
        this.initialized = true;
        // Handle anonymous users
        const userId = this.getUserId();
        const options = {};
        if (userId === null) {
            Object.assign(options, this.getAnonymousUserOptions());
        }
        else {
            Object.assign(options, this.getAuthenticatedUserOptions(userId, this.getProfileId()));
        }
        this.setAnalyticsOptions(options);
        /*
         Read experiments when session is already active (before starting it). Otherwise, they will be read on session start.
         */
        if (this.session.isActive()) {
            this.fetchExperimentVariants();
        }
        /*
          SPA: initialize will be called once at the beginning of the execution. Here we will start a brand new session.
          Non-SPA: initialize will be called on every page load. We must not close the previous session since, otherwise, we will have one-page sessions.
          This is why we are calling here `this.session.start` instead of `this.newSession`.
        */
        this.session.start();
        // Track navigation
        if (this.settings.autoTrackNavigation) {
            if (typeof window === 'undefined') {
                Log.warn(ProductAnalyticsTag, 'Cannot auto track cached navigation since DOM is not available.');
            }
            else {
                window.removeEventListener('pageshow', this.trackNavigationCached);
                window.addEventListener('pageshow', this.trackNavigationCached.bind(this));
            }
            this.trackNavigationInternal();
        }
        // Track attribution
        if (this.settings.autoTrackAttribution) {
            query = this.url === null ? '' : this.url.search;
            this.trackAttributionInternal(new URLSearchParams(query));
        }
    }
    /**
     * Identify current page
     * @private
     */
    identify() {
        let pageRule;
        let regExp;
        let i;
        // Try to identify using config approach
        if (this.config === null) {
            this.page = null;
        }
        else {
            this.page = this.config.identify(this.url);
        }
        // Try to identify using pageRules setting
        for (i = 0; i < this.settings.pageRules.length && this.page === null && this.url !== null; i++) {
            pageRule = this.settings.pageRules[i];
            if (typeof pageRule.rule !== 'undefined' && typeof pageRule.page !== 'undefined') {
                try {
                    regExp = new RegExp(pageRule.rule);
                }
                catch (ex) {
                    regExp = null;
                    Log.warn(ProductAnalyticsTag, 'Error processing rule', pageRule.rule, pageRule.page);
                }
                if (regExp !== null && this.url.pathname.match(regExp) !== null) {
                    this.page = this.url.pathname.replace(regExp, pageRule.page);
                }
            }
        }
        // If identication has failed, set pathname as page name
        if (this.page === null && this.url !== null) {
            this.page = this.url.pathname;
        }
    }
    /**
     * Destroy
     */
    destroy() {
        Log.warn(ProductAnalyticsTag, 'ProductAnalytics instance destroyed through destroy');
        // Remove adapters
        for (const videoKey of Object.keys(this.adapters)) {
            this.removeAdapter(videoKey);
        }
        // Initialize focus attributes
        this.contentFocusOut();
        // Initialize attributes
        this.initialized = false;
        this.settings = new ProductAnalyticsSettings();
        this.page = null;
        this.config = null;
        this.searchQuery = '';
        this.url = null;
    }
    // ---------------------------------------------------------------------------------------------
    // USER MANAGEMENT
    // ---------------------------------------------------------------------------------------------
    /**
     * Get anonymous user options
     * @returns
     */
    getAuthenticatedUserOptions(userId, profileId) {
        if (typeof profileId === 'undefined' || profileId === '') {
            profileId = null;
        }
        return { userId: userId, 'user.type': 'Registered', profileId: profileId };
    }
    /**
     * Get anonymous user options
     * @returns
     */
    getAnonymousUserOptions() {
        const userId = this.settings.manageAnonymousUsers ? this.getDeviceUUID() : null;
        return { userId: userId, 'user.type': 'Unregistered', profileId: null };
    }
    /**
     * Get options
     * @returns
     */
    getOptions() {
        return this.session.getOptions();
    }
    /**
     * Get current user id
     * @returns
     */
    getUserId() {
        const options = this.getOptions();
        let userId;
        if (Object.prototype.hasOwnProperty.call(options, 'userId')) {
            userId = options['userId'];
        }
        else if (Object.prototype.hasOwnProperty.call(options, 'user.name')) {
            userId = options['user.name'];
        }
        else {
            userId = null;
        }
        // Return null if userId is undefined, an empty string or 'unregistered user'
        if (typeof userId === 'undefined') {
            userId = null;
        }
        else if (typeof userId === 'string') {
            if (userId.trim() === '' || userId.trim().toLowerCase() === 'unregistered user') {
                userId = null;
            }
        }
        return userId;
    }
    /**
     * Get current profile id
     * @returns
     */
    getProfileId() {
        const options = this.getOptions();
        let profileId;
        if (Object.prototype.hasOwnProperty.call(options, 'profileId')) {
            profileId = options['profileId'];
        }
        else {
            profileId = null;
        }
        return profileId;
    }
    /**
     * Get device UUID
     * @returns
     */
    getDeviceUUID() {
        const options = this.getOptions();
        let deviceUUID;
        if (options['device.isAnonymous']) {
            deviceUUID = null;
        }
        else {
            deviceUUID = options['device.id'] || new UUIDGenerator().getKey();
        }
        return deviceUUID;
    }
    // -----------------------------------------------------------------------------------------------
    // EXPERIMENTS
    // -----------------------------------------------------------------------------------------------
    /**
     * Fetch experiment variants
     * @param userId User ID
     * @param profileId Profile ID
     * @param deviceId Device ID
     * @private
     */
    fetchExperimentVariants() {
        if (!this.settings.enableExperiments)
            return;
        this.experiments = new Experiment(Core.getInstance().getFastDataService().getAccountCode(), this.getUserId(), this.getProfileId(), this.getDeviceUUID());
        this.experiments.fetch();
    }
    // -----------------------------------------------------------------------------------------------
    // ADAPTER
    // -----------------------------------------------------------------------------------------------
    /**
     * Track adapter start
     * @private
     */
    adapterTrackStart(videoKey) {
        if (this.initialized) {
            this.trackPlayerInteraction('start', {}, {}, videoKey);
        }
    }
    /**
     * Execute after adapter is set to plugin
     * @param {object} adapter Video adapter
     * @param {string} [videoKey] Custom video identifier
     */
    registerAdapter(adapter, videoKey) {
        if (!this.initialized) {
            // Nothing to do
        }
        else if (!adapter) {
            Log.warn(ProductAnalyticsTag, 'Cannot bind adapter start since adapter is unavailable.');
        }
        else {
            // Remove previous adapter
            this.removeAdapter(videoKey);
            // Set adapter
            this.adapters[this.getVideoKey(videoKey)] = adapter;
            /*
              Add request listener to monitor START events. We have to add just one listener.
              Otherwise, START events would be fired multiple times.
             */
            if (Object.keys(this.adapters).length === 1) {
                this.addVideoRequestListener(this.videoListenerAccessor);
            }
        }
    }
    /**
     * Execute before removing adapter from plugin
     * @param {string} [videoKey] Custom video identifier
     */
    removeAdapter(videoKey) {
        videoKey = this.getVideoKey(videoKey);
        // Remove adapter from the list
        delete this.adapters[videoKey];
        // Remove pending video events for the adapter being removed
        delete this.pendingVideoEvents[videoKey];
        // Remove video listener when there are no adapters registered left
        if (Object.keys(this.adapters).length === 0) {
            this.removeVideoRequestListener(this.videoListenerAccessor);
        }
    }
    /**
     * Get adapter bound to video key
     * @param videoKey
     * @returns
     */
    getAdapter(videoKey) {
        return this.adapters[this.getVideoKey(videoKey)] || null;
    }
    /**
     * Get video key
     * @param videoKey
     * @returns
     */
    getVideoKey(videoKey) {
        return videoKey || ProductAnalytics.VIDEOKEY_DEFAULT_IDENTIFIER;
    }
    /**
     * Video listener
     * @param serviceName
     * @param videoKey
     * @param params
     */
    videoListener(serviceName, videoKey, params) {
        switch (serviceName) {
            case Constants.Service.START:
                this.adapterTrackStart(videoKey);
                break;
        }
    }
    // -----------------------------------------------------------------------------------------------
    // SESSION
    // -----------------------------------------------------------------------------------------------
    /**
     * New user session
     */
    newSession() {
        if (!this.initialized) {
            Log.warn(ProductAnalyticsTag, 'Cannot start a new session since Product Analytics is uninitialized.');
        }
        else {
            this.session.stop();
            this.session.start();
        }
    }
    /**
     * Ends user session
     */
    endSession() {
        if (!this.initialized) {
            Log.warn(ProductAnalyticsTag, 'Cannot end session since Product Analytics is uninitialized.');
        }
        else {
            this.session.stop();
        }
    }
    /**
     * Checks if the session is active
     * @returns
     */
    sessionActive() {
        return this.session.isActive();
    }
    // ---------------------------------------------------------------------------------------------
    // LOGIN / LOGOUT
    // ---------------------------------------------------------------------------------------------
    /**
     * Successful login
     * @param {string} userId The unique identifier of the user
     * @param {string} [profileId] The unique identifier of the profile
     * @param {string} [profileType] The profile type
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    loginSuccessful(userId, profileId, profileType, dimensions, metrics) {
        if (!this.checkState('log in successfully')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof userId !== 'string' && typeof userId !== 'number') {
            Log.warn(ProductAnalyticsTag, 'Cannot log in successfully since userId is unavailable.');
        }
        else if (userId === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot log in successfully since userId is unset.');
        }
        else {
            // Send user login event
            this.fireEventInternal('User Login Successful', ProductAnalyticsEventType.user, {
                username: userId,
                paIsBounce: true,
                paIgnoreSession: true
            }, dimensions, metrics);
            // Send profile selection event (in case it has been supplied)
            if (typeof profileId !== 'undefined' && profileId !== null && profileId !== '') {
                this._userProfileSelectedEvent(profileId, profileType, dimensions, metrics);
            }
            // Set the userId (and profileId) options
            const options = this.getAuthenticatedUserOptions(userId, profileId);
            this.setAnalyticsOptions(options);
            // Close the current session and start a new one
            this.newSession();
        }
    }
    /**
     * Login unsuccessful
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    loginUnsuccessful(dimensions, metrics) {
        if (this.checkState('log in unsuccessful')) {
            // Send an event informing that we are closing the session because of a profile change
            this.fireEventInternal('User Login Unsuccessful', ProductAnalyticsEventType.user, { paIsBounce: true, paIgnoreSession: false }, dimensions, metrics);
        }
    }
    /**
     * Logout
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    logout(dimensions, metrics) {
        if (this.checkState('log out')) {
            // Send an event informing that we are closing the session
            this.fireEventInternal('User Logout', ProductAnalyticsEventType.user, { paIsBounce: true, paIgnoreSession: false }, dimensions, metrics);
            // Set the userId (and profileId) options
            const options = this.getAnonymousUserOptions();
            this.setAnalyticsOptions(options);
            // Close the current session and start a new one
            this.newSession();
        }
    }
    // ---------------------------------------------------------------------------------------------
    // PROFILE
    // ---------------------------------------------------------------------------------------------
    /**
     * User profile created
     * @param {string} profileId The unique identifier of the profile
     * @param {string} [profileType] The profile type
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    userProfileCreated(profileId, profileType, dimensions, metrics) {
        if (!this.checkState('create user profile')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof profileId !== 'string' && typeof profileId !== 'number') {
            Log.warn(ProductAnalyticsTag, 'Cannot create user profile since profileId is unavailable.');
        }
        else if (profileId === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot create user profile since profileId is unset.');
        }
        else {
            const dimensionsInternal = this._getUserProfileDimensions(profileId, profileType, false);
            this.fireEventInternal('User Profile Created', ProductAnalyticsEventType.userProfile, dimensionsInternal, dimensions, metrics);
        }
    }
    /**
     * User profile selected
     * @param {string} profileId The unique identifier of the profile
     * @param {string} [profileType] The profile type
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    userProfileSelected(profileId, profileType, dimensions, metrics) {
        if (!this.checkState('select user profile')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof profileId !== 'string' && typeof profileId !== 'number') {
            Log.warn(ProductAnalyticsTag, 'Cannot select user profile since profileId is unavailable.');
        }
        else if (profileId === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot select user profile since profileId is unset.');
        }
        else {
            // Send an event informing that we are closing the session because of a profile change
            this._userProfileSelectedEvent(profileId, profileType, dimensions, metrics);
            // Set the profileId option
            const options = this.getAuthenticatedUserOptions(this.getUserId(), profileId);
            this.setAnalyticsOptions(options);
            // Close the current session and open a new one
            this.newSession();
        }
    }
    /**
     * Fire user profile selected event
     * @param {string} profileId The unique identifier of the profile
     * @param {string} [profileType] The profile type
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     * @private
     */
    _userProfileSelectedEvent(profileId, profileType, dimensions, metrics) {
        const dimensionsInternal = this._getUserProfileDimensions(profileId, profileType, true);
        this.fireEventInternal('User Profile Selected', ProductAnalyticsEventType.userProfile, dimensionsInternal, dimensions, metrics);
    }
    /**
     * User profile deleted
     * @param {string} profileId The unique identifier of the profile
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    userProfileDeleted(profileId, dimensions, metrics) {
        if (!this.checkState('delete user profile')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof profileId !== 'string' && typeof profileId !== 'number') {
            Log.warn(ProductAnalyticsTag, 'Cannot delete user profile since profileId is unavailable.');
        }
        else if (profileId === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot delete user profile since profileId is unset.');
        }
        else {
            const dimensionsInternal = this._getUserProfileDimensions(profileId, null, false);
            this.fireEventInternal('User Profile Deleted', ProductAnalyticsEventType.userProfile, dimensionsInternal, dimensions, metrics);
        }
    }
    /**
     * Get user profile dimensions
     * @param {string} profileId
     * @param {string} profileType
     * @param {boolean} ignoreSession
     * @returns
     * @private
     */
    _getUserProfileDimensions(profileId, profileType, ignoreSession) {
        const dimensions = {
            profileId: profileId,
            paIsBounce: true,
            paIgnoreSession: typeof ignoreSession === 'boolean' ? ignoreSession : false
        };
        if (typeof profileType !== 'undefined' && profileType !== null) {
            dimensions['profileType'] = profileType;
        }
        return dimensions;
    }
    // -----------------------------------------------------------------------------------------------
    // NAVIGATION
    // -----------------------------------------------------------------------------------------------
    /**
     * Tracks navigation by route
     * @param {string} pageName The unique name to identify a page of the application.
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackNavByRoute(route, dimensions, metrics) {
        if (!this.checkState('track navigation')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof route !== 'string' || route === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot track navigation since route has not been supplied.');
        }
        else {
            let url;
            try {
                url = new URL(route);
            }
            catch (ex) {
                url = null;
            }
            if (url === null) {
                Log.warn(ProductAnalyticsTag, 'Cannot track navigation since route is invalid.');
            }
            else {
                this.url = url;
                this.identify();
                this.trackNavigationInternal(dimensions, metrics);
            }
        }
    }
    /**
     * Tracks navigation
     * @param {string} pageName The unique name to identify a page of the application.
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackNavByName(pageName, dimensions, metrics) {
        if (!this.checkState('track navigation')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof pageName !== 'string' || pageName === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot track navigation since page is invalid.');
        }
        else {
            this.page = pageName;
            this.trackNavigationInternal(dimensions, metrics);
        }
    }
    /**
     * Automatically tracks navigation when page is cached by browser
     * @param {Object} event
     * @private
     */
    trackNavigationCached(event) {
        if (event.persisted) {
            this.trackNavigationInternal();
        }
    }
    /**
     * Tracks navigation (either manually or automatically)
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     * @private
     */
    trackNavigationInternal(dimensions, metrics) {
        let route;
        let path;
        let host;
        // Get UTM Options
        if (this.url === null) {
            route = '';
            path = '';
            host = '';
        }
        else {
            route = this.url.href;
            path = this.url.pathname;
            host = this.url.hostname;
        }
        if (this.checkState('track navigation')) {
            this.fireEventInternal('paNavigation', ProductAnalyticsEventType.navigation, {
                paIsBounce: false,
                paIgnoreSession: false,
                paRoute: path,
                paRouteDomain: host,
                paFullRoute: route,
                paReferrer: this.getReferral() || ''
            }, dimensions, metrics, true);
        }
    }
    // -----------------------------------------------------------------------------------------------
    // ATTRIBUTION
    // -----------------------------------------------------------------------------------------------
    /**
     * Tracks attribution
     * @param {string} utmSource The UTM Source parameter. It is commonly used to identify a search engine, newsletter, or other source (i.e., Google, Facebook, etc.).
     * @param {string} utmMedium The UTM Medium parameter. It is commonly used to identify a medium such as email or cost-per-click (cpc).
     * @param {string} utmCampaign The UTM Campaign parameter. It is commonly used for campaign analysis to identify a specific product promotion or strategic campaign (i.e., spring sale).
     * @param {string} utmTerm The UTM Term parameter. It is commonly used with paid search to supply the keywords for ads (i.e., Customer, NonBuyer, etc.).
     * @param {string} utmContent The UTM Content parameter. It is commonly used for A/B testing and content-targeted ads to differentiate ads or links that point to the same URL (i.e., Banner1, Banner2, etc.)
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackAttribution(utmSource, utmMedium, utmCampaign, utmTerm, utmContent, dimensions, metrics) {
        if (!this.checkState('track attribution')) {
            // Product Analytics is not ready for sending events
        }
        else if (this.settings.autoTrackAttribution) {
            Log.warn(ProductAnalyticsTag, "Automatic attribution tracking is enabled: this request won't be processed.");
        }
        else {
            const params = {};
            if (typeof utmSource === 'string')
                params.utm_source = utmSource;
            if (typeof utmMedium === 'string')
                params.utm_medium = utmMedium;
            if (typeof utmCampaign === 'string')
                params.utm_campaign = utmCampaign;
            if (typeof utmTerm === 'string')
                params.utm_term = utmTerm;
            if (typeof utmContent === 'string')
                params.utm_content = utmContent;
            if (Object.keys(params).length > 0) {
                this.trackAttributionInternal(new URLSearchParams(params), dimensions, metrics);
            }
            else {
                Log.warn(ProductAnalyticsTag, 'Cannot track attribution since parameters are invalid.');
            }
        }
    }
    /**
     * Tracks attribution (either manually or automatically)
     * @param {URLSearchParams} params Object where to look for UTM params.
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     * @private
     */
    trackAttributionInternal(params, dimensions, metrics) {
        // Get UTM parameters
        const utmParams = this._getUTMParams(params);
        const url = this.url === null ? '' : this.url.href;
        // Track attribution
        if (!this.checkState('track attribution')) {
            // Product Analytics is not ready for sending events
        }
        else if (utmParams) {
            this.fireEventInternal('Attribution', ProductAnalyticsEventType.attribution, Object.assign({
                paIsBounce: true,
                paIgnoreSession: false,
                utmUrl: url
            }, utmParams), dimensions, metrics);
        }
    }
    /**
     * Retrieves UTM params from querystring (utm_source, utm_medium, utm_campaign, utm_term, utm_content) and returns an object containing them but
     * with key formatted as specified by utmFormatDot.
     * @param {URLSearchParams} params Object where to look for UTM params
     * @returns {{}}
     * @private
     */
    _getUTMParams(params) {
        let keySplitted;
        let keyIndex;
        let key;
        const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
        const utmParams = {};
        // Look for UTM params and add them to the newly constructed object converting key as specified by utmFormatDot
        params.forEach(function (paramValue, paramKey) {
            keyIndex = keys.indexOf(paramKey.toLowerCase());
            if (keyIndex !== -1) {
                key = keys[keyIndex];
                keySplitted = key.split('_');
                if (keySplitted.length > 1) {
                    key = keySplitted[0] + keySplitted[1].charAt(0).toUpperCase() + keySplitted[1].slice(1);
                }
                utmParams[key] = paramValue;
            }
        });
        return Object.keys(utmParams).length > 0 ? utmParams : null;
    }
    // -----------------------------------------------------------------------------------------------
    // SECTION
    // -----------------------------------------------------------------------------------------------
    /**
     * Section goes into viewport.
     * @param {string} section The section title. It is commonly used to indicate the section title presented over a grid layout (e.g. Recommended Movies, Continue Watching, etc).
     * @param {Number} sectionOrder The section order within the page.
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackSectionVisible(section, sectionOrder, dimensions, metrics) {
        if (!this.checkState('track section visible')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof section !== 'string' || section === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot track section visible since section is invalid.');
        }
        else if (!ProductAnalytics.checkCoordinate(sectionOrder)) {
            Log.warn(ProductAnalyticsTag, 'Cannot track section visible since sectionOrder is invalid.');
        }
        else {
            this.fireEventInternal('Section Visible', ProductAnalyticsEventType.section, {
                paIsBounce: true,
                paIgnoreSession: false,
                sectionOrder: sectionOrder,
                section: section
            }, dimensions, metrics);
        }
    }
    /**
     * Section goes out of viewport.
     * @param {string} section The section title. It is commonly used to indicate the section title presented over a grid layout (e.g. Recommended Movies, Continue Watching, etc).
     * @param {Number} sectionOrder The section order within the page.
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackSectionHidden(section, sectionOrder, dimensions, metrics) {
        if (!this.checkState('track section hidden')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof section !== 'string' || section === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot track section hidden since section is invalid.');
        }
        else if (!ProductAnalytics.checkCoordinate(sectionOrder)) {
            Log.warn(ProductAnalyticsTag, 'Cannot track section hidden since sectionOrder is invalid.');
        }
        else {
            this.fireEventInternal('Section Hidden', ProductAnalyticsEventType.section, {
                paIsBounce: true,
                paIgnoreSession: false,
                sectionOrder: sectionOrder,
                section: section
            }, dimensions, metrics);
        }
    }
    // -----------------------------------------------------------------------------------------------
    // CONTENT
    // -----------------------------------------------------------------------------------------------
    /**
     * Sends a content highlight event if content is focused during, at least, highlightContentAfter ms.
     * @param {string} section The section title. It is commonly used to indicate the section title presented over a grid layout (e.g. Recommended Movies, Continue Watching, etc).
     * @param {number} sectionOrder The section order within the page.
     * @param {string} column Used to indicate the column number where content is placed in a grid layout The first column is number 0.
     * @param {string} row Used to indicate the row number where content is placed in a grid layout. The first row is number 0. In the case of a horizontal list instead of a grid, the row parameter should be set to 0.
     * @param {string} contentLink The unique content identifier of the content linked.
     * @param {string} contentLinkType The type of content link (i.e., contentId, movieGroupTitle, movieGroupId, seriesTitle, seriesId, seriesSeasonTitle, seriesSeasonId).
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    contentFocusIn(section, sectionOrder, column, row, contentLink, contentLinkType, dimensions, metrics) {
        this.contentFocusOut();
        if (!this.checkState('track content highlight')) {
            // Product Analytics is not ready for sending events
        }
        else if (!this.checkSectionCoordinates(section, sectionOrder, column, row, contentLink, 'content highlight')) {
            // Section coordinates are invalid
        }
        else {
            this.contentHighlighted = {
                sectionOrder: sectionOrder,
                section: section,
                column: column,
                row: row,
                contentLink: contentLink,
                contentLinkType: contentLinkType,
                dimensions: dimensions,
                metrics: metrics
            };
            this.contentHighlightTimeout = setTimeout(this._trackContentHiglight.bind(this), this.settings.highlightContentAfter);
        }
    }
    /**
     * Content loses focus
     * @private
     */
    contentFocusOut() {
        if (this.contentHighlightTimeout !== null) {
            clearTimeout(this.contentHighlightTimeout);
        }
        this.contentHighlighted = null;
        this.contentHighlightTimeout = null;
    }
    /**
     * Sends a content highlight event using selected content info
     * @private
     */
    _trackContentHiglight() {
        if (this.contentHighlighted) {
            this.fireEventInternal('Section Content Highlight', ProductAnalyticsEventType.section, ProductAnalytics.buildSectionPlacementDimensions(true, false, this.contentHighlighted.section, this.contentHighlighted.sectionOrder, this.contentHighlighted.column, this.contentHighlighted.row, this.contentHighlighted.contentLink, this.contentHighlighted.contentLinkType), this.contentHighlighted.dimensions, this.contentHighlighted.metrics);
        }
        else {
            Log.warn(ProductAnalyticsTag, 'Cannot highlight content since no content is selected');
        }
    }
    /**
     * Tracks the location of user clicks.
     * @param {string} section The section title. It is commonly used to indicate the section title presented over a grid layout (e.g. Recommended Movies, Continue Watching, etc).
     * @param {number} sectionOrder The section order within the page.
     * @param {number} column Used to indicate the column number where content is placed in a grid layout The first column is number 0.
     * @param {number} row Used to indicate the row number where content is placed in a grid layout. The first row is number 0. In the case of a horizontal list instead of a grid, the row parameter should be set to 0.
     * @param {string} contentLink The unique content identifier of the content linked.
     * @param {string} [contentLinkType] The type of content link (i.e., contentId, movieGroupTitle, movieGroupId, seriesTitle, seriesId, seriesSeasonTitle, seriesSeasonId).
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackContentClick(section, sectionOrder, column, row, contentLink, contentLinkType, dimensions, metrics) {
        if (!this.checkState(`track content click`)) {
            // Product Analytics is not ready for sending events
        }
        else if (!this.checkSectionCoordinates(section, sectionOrder, column, row, contentLink, 'content click')) {
            // Section coordinates are invalid
        }
        else {
            this.fireEventInternal('Section Content Click', ProductAnalyticsEventType.section, ProductAnalytics.buildSectionPlacementDimensions(false, false, section, sectionOrder, column, row, contentLink, contentLinkType), dimensions, metrics);
        }
    }
    // -----------------------------------------------------------------------------------------------
    // CONTENT PLAYBACK
    // -----------------------------------------------------------------------------------------------
    /**
     * Tracks when a VOD content starts playing be it automatically or through a user interaction.
     * @param {string} contentId The unique content identifier of the content being played.
     * @param {string} [movieGroupTitle] The movie group title.
     * @param {string} [movieGroupId] The movie group identifier.
     * @param {string} [seriesSeasonTitle] The series season title.
     * @param {string} [seriesSeasonId] The series season identifier.
     * @param {string} [seriesTitle] The series title.
     * @param {string} [seriesId] The series identifier.
     * @param {object} [dimensions] Dimensions to track
     * @param {object} [metrics] Metrics to track
     * @param {string} [videoKey] Custom video identifier.
     */
    trackPlayVOD(contentId, movieGroupTitle, movieGroupId, seriesSeasonTitle, seriesSeasonId, seriesTitle, seriesId, dimensions, metrics, videoKey) {
        const links = [];
        links.push({ contentLink: contentId, contentLinkType: 'contentId' });
        links.push({ contentLink: movieGroupTitle, contentLinkType: 'movieGroupTitle' });
        links.push({ contentLink: movieGroupId, contentLinkType: 'movieGroupId' });
        links.push({ contentLink: seriesSeasonTitle, contentLinkType: 'seriesSeasonTitle' });
        links.push({ contentLink: seriesSeasonId, contentLinkType: 'seriesSeasonId' });
        links.push({ contentLink: seriesTitle, contentLinkType: 'seriesTitle' });
        links.push({ contentLink: seriesId, contentLinkType: 'seriesId' });
        this.trackPlayItems(links, dimensions, metrics, videoKey);
    }
    /**
     * Tracks when an EPG content starts playing be it automatically or through a user interaction.
     * @param {string | null} channelId The channel identifier.
     * @param {string | null} epgId The EPG identifier.
     * @param {object} [dimensions] Dimensions to track
     * @param {object} [metrics] Metrics to track
     * @param {string} [videoKey] Custom video identifier.
     */
    trackPlayEPG(channelId, epgId, dimensions, metrics, videoKey) {
        const links = [];
        links.push({ contentLink: channelId, contentLinkType: 'channelId' });
        links.push({ contentLink: epgId, contentLinkType: 'epgId' });
        this.trackPlayItems(links, dimensions, metrics, videoKey);
    }
    /**
     * Track play items
     * @param {object[]} contentLinks An array of content links to track.
     * @param {object} [dimensions] Dimensions to track.
     * @param {object} [metrics] Metrics to track.
     * @param {string} [videoKey] Custom video identifier.
     */
    trackPlayItems(contentLinks, dimensions, metrics, videoKey) {
        contentLinks.forEach((link) => {
            if (ProductAnalytics.checkContentLink(link['contentLink']) &&
                ProductAnalytics.checkContentLinkType(link['contentLinkType'])) {
                this.trackPlay(link['contentLink'], link['contentLinkType'], dimensions, metrics, videoKey);
            }
        });
    }
    /**
     * Tracks when a content starts playing be it automatically or through a user interaction.
     * @param {string} contentLink The content identifier of the content being played.
     * @param {string} [contentLinkType] The type of content link (i.e., contentId, movieGroupTitle, movieGroupId, seriesTitle, seriesId, seriesSeasonTitle, seriesSeasonId).
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     * @param {string} [videoKey] Custom video identifier
     */
    trackPlay(contentLink, contentLinkType, dimensions, metrics, videoKey) {
        if (!this.checkState('track play')) {
            // Product Analytics is not ready for sending events
        }
        else if (!ProductAnalytics.checkContentLink(contentLink)) {
            Log.warn(ProductAnalyticsTag, 'Cannot track play since contentLink is invalid.');
        }
        else {
            // Content Play
            this.handlePlayerEvent('Content Play', contentLink, contentLinkType, dimensions || {}, metrics || {}, this.getVideoKey(videoKey));
        }
    }
    /**
     * Tracks content watching events.
     * @param {string} eventName The name of the interaction (i.e., Pause, Seek, Skip Intro, Skip Ads, Switch Language, etc.).
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     * @param {string} [videoKey] Custom video identifier
     * @param {boolean} [startEvent] Internal param informing that current interaction is responsible of first player start
     */
    trackPlayerInteraction(eventName, dimensions, metrics, videoKey) {
        if (!this.checkState('track player interaction')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof eventName !== 'string' || eventName === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot track player interaction since interaction name is invalid.');
        }
        else {
            const eventNameComplete = 'Content Play ' + eventName;
            const contentLink = null;
            const contentLinkType = null;
            this.handlePlayerEvent(eventNameComplete, contentLink, contentLinkType, dimensions || {}, metrics || {}, this.getVideoKey(videoKey));
        }
    }
    /**
     * Handle player event (queueing event in case video has not started yet or flushing the event
     * queue in case video starts)
     */
    handlePlayerEvent(eventName, contentLink, contentLinkType, dimensions, metrics, videoKey) {
        var _a;
        const videoAdapter = this.getAdapter(videoKey);
        if (videoAdapter == null) {
            // Adapter is not available: cannot track event
            Log.warn(ProductAnalyticsTag, 'Cannot track player event since video adapter is not available');
        }
        else if (!videoAdapter.flags.isStarted) {
            // Player has not started: queue request
            if (this.pendingVideoEvents[videoKey] == null) {
                this.pendingVideoEvents[videoKey] = [];
            }
            this.pendingVideoEvents[videoKey].push({
                eventName: eventName,
                contentLink: contentLink,
                contentLinkType: contentLinkType,
                dimensions: dimensions,
                metrics: metrics,
                videoKey: videoKey
            });
        }
        else {
            // Track pending events
            (_a = this.pendingVideoEvents[videoKey]) === null || _a === void 0 ? void 0 : _a.forEach((event) => {
                this.trackPlayerEvent(event.eventName, event.contentLink, event.contentLinkType, event.dimensions, event.metrics, event.videoKey);
            });
            delete this.pendingVideoEvents[videoKey];
            // Track current event
            this.trackPlayerEvent(eventName, contentLink, contentLinkType, dimensions, metrics, videoKey);
        }
    }
    /**
     * Track player event
     *
     * @param eventName The name of the interaction (i.e., Pause, Seek, Skip Intro, Skip Ads, Switch Language, etc.).
     * @param contentLink The content identifier of the content being played.
     * @param contentLinkType The type of content link (i.e., contentId, movieGroupTitle, movieGroupId, seriesTitle, seriesId, seriesSeasonTitle, seriesSeasonId).
     * @param dimensions Dimensions to track
     * @param metrics Metrics to track
     * @param videoKey Video adapter identifier
     */
    trackPlayerEvent(eventName, contentLink, contentLinkType, dimensions, metrics, videoKey) {
        // Prepare dimensions
        const dimensionsInternal = { paIsBounce: false, paIgnoreSession: false };
        if (contentLink !== null) {
            Object.assign(dimensionsInternal, ProductAnalytics.buildContentLinkDimensions(contentLink, contentLinkType));
        }
        // Fire event
        this.fireEventAdapter(eventName, dimensionsInternal, dimensions, metrics, videoKey);
    }
    // -----------------------------------------------------------------------------------------------
    // CONTENT SEARCH
    // -----------------------------------------------------------------------------------------------
    /**
     * Tracks search query events.
     * @param {string} searchQuery The search term entered by the user.
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackSearchQuery(searchQuery, dimensions, metrics) {
        if (!this.checkState('track search query')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof searchQuery !== 'string' || searchQuery === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot track search query since no searchQuery has been supplied.');
        }
        else {
            this.searchQuery = searchQuery;
            this.fireEventInternal('Search Query', ProductAnalyticsEventType.search, {
                paIsBounce: false,
                paIgnoreSession: false,
                query: this.searchQuery
            }, dimensions, metrics);
        }
    }
    /**
     * Tracks search result events.
     * @param {integer} resultCount The number of search results returned by a search query.
     * @param {String} [searchQuery] The search term entered by the user.
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackSearchResult(resultCount, searchQuery, dimensions, metrics) {
        if (!this.checkState('track search result')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof resultCount !== 'number') {
            Log.warn(ProductAnalyticsTag, 'Cannot track search result since resultCount is invalid.');
        }
        else {
            const query = typeof searchQuery === 'string' && searchQuery !== '' ? searchQuery : this.searchQuery;
            this.fireEventInternal('Search Results', ProductAnalyticsEventType.search, {
                paIsBounce: true,
                paIgnoreSession: false,
                query: query,
                resultCount: resultCount
            }, dimensions, metrics);
        }
    }
    /**
     * Tracks user interactions with search results.
     * @param {string} section The section title. It is commonly used to indicate the section title presented over a grid layout (e.g. Recommended Movies, Continue Watching, etc).
     * @param {number} sectionOrder The section order within the page.
     * @param {number} column The content placement column. It is commonly used to indicate the column number where content is placed in a grid layout (i.e.1, 2, etc..).
     * @param {number} row The content placement row. It is commonly used to indicate the row number where content is placed in a grid layout (i.e.1, 2, etc..).
     * @param {string} contentLink The content identifier. It is used for internal content unequivocally identification (i.e., AAA000111222).
     * @param {string} [contentLinkType] The type of content link (i.e., contentId, movieGroupTitle, movieGroupId, seriesTitle, seriesId, seriesSeasonTitle, seriesSeasonId).
     * @param {String} [searchQuery] The search term entered by the user.
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackSearchClick(section, sectionOrder, column, row, contentLink, contentLinkType, searchQuery, dimensions, metrics) {
        const sectionEnforced = typeof section === 'string' && section !== '' ? section : 'Search';
        const sectionOrderEnforced = ProductAnalytics.checkCoordinate(sectionOrder) ? sectionOrder : 0;
        if (!this.checkState(`track search click`)) {
            // Product Analytics is not ready for sending events
        }
        else if (!this.checkSectionCoordinates(sectionEnforced, sectionOrderEnforced, column, row, contentLink, 'search click')) {
            // Section coordinates are invalid
        }
        else {
            const query = typeof searchQuery === 'string' && searchQuery !== '' ? searchQuery : this.searchQuery;
            const dimensionsInternal = ProductAnalytics.buildSectionPlacementDimensions(false, false, sectionEnforced, sectionOrderEnforced, column, row, contentLink, contentLinkType);
            dimensionsInternal['query'] = query;
            this.fireEventInternal('Search Result Click', ProductAnalyticsEventType.search, dimensionsInternal, dimensions, metrics);
        }
    }
    // -----------------------------------------------------------------------------------------------
    // EPG
    // -----------------------------------------------------------------------------------------------
    /**
     * Tracks the location of user clicks on EPG content.
     * @param {string} channelId The channel identifier.
     * @param {string} epgId The EPG item ID.
     * @param {string} epgMode The EPG play mode (past / live / future)
     * @param {string} contentLink The unique content identifier of the content linked.
     * @param {string} [contentLinkType] The type of content link (i.e., contentId, movieGroupTitle, movieGroupId, seriesTitle, seriesId, seriesSeasonTitle, seriesSeasonId).
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackEPGClick(channelId, epgId, epgMode, contentId, contentLink, contentLinkType, dimensions, metrics) {
        const message = 'track EPG content click';
        if (!this.checkState(message)) {
            // Product Analytics is not ready for sending events
        }
        else if (!ProductAnalytics.checkContentLink(contentLink)) {
            Log.warn(ProductAnalyticsTag, `Cannot ${message} since content link is invalid.`);
        }
        else if (!ProductAnalytics.checkContentLinkType(contentLinkType)) {
            Log.warn(ProductAnalyticsTag, `Cannot ${message} since content link type is invalid.`);
        }
        else {
            const dimensionsInternal = {
                paIsBounce: false,
                paIgnoreSession: false,
                contentLink: contentLink,
                contentLinkType: contentLinkType
            };
            if ((typeof channelId === 'string' && channelId !== '') || typeof channelId === 'number') {
                dimensionsInternal['channelId'] = channelId;
            }
            if (ProductAnalytics.checkEpgId(epgId)) {
                dimensionsInternal['epgId'] = epgId;
            }
            if (typeof epgMode === 'string' && epgMode !== '') {
                dimensionsInternal['epgMode'] = epgMode;
            }
            if (ProductAnalytics.checkContentId(contentId)) {
                dimensionsInternal['contentId'] = contentId;
            }
            this.fireEventInternal('EPG Click', ProductAnalyticsEventType.epg, dimensionsInternal, dimensions, metrics);
        }
    }
    // -----------------------------------------------------------------------------------------------
    // EXTERNAL APPLICATIONS
    // -----------------------------------------------------------------------------------------------
    /**
     * Tracks external app start events.
     * @param {string} appName The name of the application being used to deliver the content to the end-user (i.e., Netflix).
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackExternalAppLaunch(appName, dimensions, metrics) {
        if (!this.checkState('track external application launch')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof appName !== 'string' || appName === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot track external application launch since appName is invalid.');
        }
        else {
            this.fireEventInternal('External Application Launch', ProductAnalyticsEventType.externalApplication, {
                paIsBounce: false,
                paIgnoreSession: false,
                paExtAppName: appName
            }, dimensions, metrics);
        }
    }
    /**
     * Tracks external app stop events.
     * @param {string} appName The name of the application being used to deliver the content to the end-user (i.e., Netflix).
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackExternalAppExit(appName, dimensions, metrics) {
        if (!this.checkState('track external application exit')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof appName !== 'string' || appName === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot track external application exit since appName is invalid.');
        }
        else {
            this.fireEventInternal('External Application Exit', ProductAnalyticsEventType.externalApplication, {
                paIsBounce: false,
                paIgnoreSession: false,
                paExtAppName: appName
            }, dimensions, metrics);
        }
    }
    // -----------------------------------------------------------------------------------------------
    // ENGAGEMENT
    // -----------------------------------------------------------------------------------------------
    /**
     * Tracks engagement events.
     * @param {string} eventName The name of the engagement event (i.e., Share, Save, Rate, etc.).
     * @param {string} [contentId] The unique content identifier of the content the user is engaging with.
     * @param {string} [epgId] The EPG item ID.
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     */
    trackEngagementEvent(eventName, contentId, epgId, dimensions, metrics) {
        if (!this.checkState('track engagement')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof eventName !== 'string' || eventName === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot track engagement event since eventName is invalid.');
        }
        else {
            let identified = false;
            const dimensionsInternal = {
                paIsBounce: false,
                paIgnoreSession: false
            };
            if (ProductAnalytics.checkContentId(contentId)) {
                dimensionsInternal['contentId'] = contentId;
                identified = true;
            }
            if (ProductAnalytics.checkEpgId(epgId)) {
                dimensionsInternal['epgId'] = epgId;
                identified = true;
            }
            if (!identified) {
                Log.warn(ProductAnalyticsTag, 'Tracking engagement event without contentId or epgId.');
            }
            this.fireEventInternal('Engagement ' + eventName, ProductAnalyticsEventType.engagement, dimensionsInternal, dimensions, metrics);
        }
    }
    // -----------------------------------------------------------------------------------------------
    // INTERACTION
    // -----------------------------------------------------------------------------------------------
    /**
     * Track click event
     * @param {string} controlName Name of the control receiving the click
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     * @param {boolean} [hasEndDatetime] For events with duration
     */
    trackClick(controlName, dimensions, metrics, hasEndDatetime) {
        if (typeof hasEndDatetime !== 'boolean') {
            hasEndDatetime = false;
        }
        if (!this.checkState('track click event')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof controlName !== 'string' || controlName === '') {
            Log.warn(ProductAnalyticsTag, 'Click cannot be tracked since controlName is invalid.');
        }
        else {
            this.fireEventInternal('Click', ProductAnalyticsEventType.interaction, { paIsBounce: false, paIgnoreSession: false, controlName: controlName }, dimensions, metrics, hasEndDatetime);
        }
    }
    // -----------------------------------------------------------------------------------------------
    // CUSTOM EVENT
    // -----------------------------------------------------------------------------------------------
    /**
     * Track custom event
     * @param {string} eventName Name of the event to track
     * @param {Object} [dimensions] Dimensions to track
     * @param {Object} [metrics] Metrics to track
     * @param {boolean} [hasEndDatetime] For events with duration
     */
    trackEvent(eventName, dimensions, metrics, hasEndDatetime) {
        if (typeof hasEndDatetime !== 'boolean') {
            hasEndDatetime = false;
        }
        if (!this.checkState('track custom event')) {
            // Product Analytics is not ready for sending events
        }
        else if (typeof eventName !== 'string' || eventName === '') {
            Log.warn(ProductAnalyticsTag, 'Event cannot be tracked since eventName is invalid.');
        }
        else {
            this.fireEventInternal('Custom ' + eventName, ProductAnalyticsEventType.custom, { paIsBounce: false, paIgnoreSession: false }, dimensions, metrics, hasEndDatetime);
        }
    }
    // -----------------------------------------------------------------------------------------------
    // INTERNAL
    // -----------------------------------------------------------------------------------------------
    /**
     * Fires an event
     * @param {string} eventName Name of the event to be fired
     * @param {ProductAnalyticsEventType} eventType Type of the event being tracked
     * @param {Object} dimensionsInternal Dimensions supplied by user
     * @param {Object} dimensionsUser Specific event dimensions
     * @param {Object} metrics Metrics to track
     * @param {boolean} hasEndDatetime For events with duration
     * @private
     */
    fireEventInternal(eventName, eventType, dimensionsInternal = {}, dimensionsUser = {}, metrics = {}, hasEndDatetime = false) {
        Log.notice(ProductAnalyticsTag, eventName);
        // Extract top level dimensions from custom dimensions
        const dimensions = this.buildDimensions(eventType, dimensionsInternal, dimensionsUser);
        // Track event
        this.session.sendEvent(eventName, dimensions.custom, metrics, dimensions.top, hasEndDatetime);
    }
    /**
     * Fires an adapter event (in case it is available)
     * @param {string} eventName Event name
     * @param {Object} dimensionsInternal Dimensions supplied by user
     * @param {Object} dimensionsUser Specific event dimensions
     * @param {Object} metrics Metrics to track
     * @param {string} [videoKey] Custom video identifier
     * @private
     */
    fireEventAdapter(eventName, dimensionsInternal, dimensionsUser, metrics, videoKey) {
        Log.notice(ProductAnalyticsTag, eventName);
        const adapter = this.getAdapter(videoKey);
        if (adapter) {
            const dimensions = this.buildDimensions(ProductAnalyticsEventType.contentPlayback, dimensionsInternal, dimensionsUser);
            adapter.fireEvent(eventName, dimensions.custom, metrics, dimensions.top);
        }
        else {
            Log.warn(ProductAnalyticsTag, 'Cannot fire ' + eventName + ' event since adapter is unavailable.');
        }
    }
    /**
     * Builds a list of top level and custom dimensions
     * @param {ProductAnalyticsEventType} eventType Type of the event being tracked
     * @param {Object} dimensionsInternal Object containing list of internal dimensions
     * @param {Object} dimensionsUser Object containing list of custom dimensions
     * @private
     */
    buildDimensions(eventType, dimensionsInternal = {}, dimensionsUser = {}) {
        let dimensionsCustom;
        // Build custom event dimensions
        dimensionsCustom = { paPage: this.page };
        if (typeof dimensionsInternal['contentLinkType'] === 'string') {
            dimensionsInternal['contentLinkType'] = dimensionsInternal['contentLinkType'].toLowerCase();
        }
        dimensionsCustom = Object.assign(dimensionsCustom, dimensionsInternal);
        dimensionsCustom = Object.assign(dimensionsCustom, dimensionsUser);
        dimensionsCustom = Object.assign(dimensionsCustom, { eventType: eventType, eventSource: 'Product Analytics' });
        // List of Top Level Dimension keys
        const dimensionsTopLevelKeys = [
            'contentid',
            'contentId',
            'contentID',
            'utmSource',
            'utmMedium',
            'utmCampaign',
            'utmTerm',
            'utmContent',
            'profileId',
            'profile_id',
            'username'
        ];
        // Create object with top level dimensions
        const dimensionsTopLevel = {};
        for (const key in dimensionsCustom) {
            if (Object.prototype.hasOwnProperty.call(dimensionsCustom, key) && dimensionsTopLevelKeys.indexOf(key) !== -1) {
                dimensionsTopLevel[key] = dimensionsCustom[key];
            }
        }
        // Remove top level dimensions from custom dimensions list
        dimensionsTopLevelKeys.forEach(function (key) {
            delete dimensionsCustom[key];
        });
        return { custom: dimensionsCustom, top: dimensionsTopLevel };
    }
    /**
     * Build section placement dimensions
     * @param paIsBounce The bounce flag.
     * @param paIgnoreSession The ignore session flag.
     * @param section The section title. It is commonly used to indicate the section title presented over a grid layout (e.g. Recommended Movies, Continue Watching, etc).
     * @param sectionOrder The section order within the page.
     * @param column The content placement column. It is commonly used to indicate the column number where content is placed in a grid layout (i.e.1, 2, etc..).
     * @param row The content placement row. It is commonly used to indicate the row number where content is placed in a grid layout (i.e.1, 2, etc..).
     * @param contentLink The content identifier. It is used for internal content unequivocally identification (i.e., AAA000111222).
     * @param contentLinkType The type of content link (i.e., contentId, movieGroupTitle, movieGroupId, seriesTitle, seriesId, seriesSeasonTitle, seriesSeasonId).
     * @returns The section placement dimensions.
     */
    static buildSectionPlacementDimensions(paIsBounce, paIgnoreSession, section, sectionOrder, column, row, contentLink, contentLinkType) {
        const dimensionsInternal = {
            paIsBounce: paIsBounce,
            paIgnoreSession: paIgnoreSession,
            sectionOrder: sectionOrder,
            section: section,
            column: column,
            row: row
        };
        Object.assign(dimensionsInternal, ProductAnalytics.buildContentLinkDimensions(contentLink, contentLinkType));
        return dimensionsInternal;
    }
    /**
     * Build content link dimensions
     * @param contentLink The content identifier of the content being played.
     * @param contentLinkType The type of content link (i.e., contentId, movieGroupTitle, movieGroupId, seriesTitle, seriesId, seriesSeasonTitle, seriesSeasonId).
     * @returns The content link dimensions.
     */
    static buildContentLinkDimensions(contentLink, contentLinkType) {
        if (!ProductAnalytics.checkContentLinkType(contentLinkType)) {
            contentLinkType = 'contentId';
        }
        const dimensionsContentLink = {
            contentLink: contentLink,
            contentLinkType: contentLinkType
        };
        if (typeof contentLinkType === 'string' && contentLinkType.toLowerCase() === 'contentid') {
            dimensionsContentLink['contentId'] = contentLink;
        }
        return dimensionsContentLink;
    }
    /**
     * Check state before sending an event
     * @param {String} message Message to show in case validation fails
     * @private
     */
    checkState(message) {
        let valid;
        if (!this.initialized) {
            valid = false;
            Log.warn('Cannot ' + message + ' since Product Analytics is uninitialized.');
        }
        else {
            valid = true;
        }
        return valid;
    }
    /**
     * Check coordinates
     * @param {string} section The section title. It is commonly used to indicate the section title presented over a grid layout (e.g. Recommended Movies, Continue Watching, etc).
     * @param {number} sectionOrder The section order within the page.
     * @param {number} column The content placement column. It is commonly used to indicate the column number where content is placed in a grid layout (i.e.1, 2, etc..).
     * @param {number} row The content placement row. It is commonly used to indicate the row number where content is placed in a grid layout (i.e.1, 2, etc..).
     * @param {string} contentLink The content identifier. It is used for internal content unequivocally identification (i.e., AAA000111222).
     * @param {string} message Message to show in case validation fails
     * @param message
     */
    checkSectionCoordinates(section, sectionOrder, column, row, contentLink, message) {
        let valid = false;
        if (typeof section !== 'string' || section === '') {
            Log.warn(ProductAnalyticsTag, 'Cannot track ' + message + ' since section is invalid.');
        }
        else if (!ProductAnalytics.checkCoordinate(sectionOrder)) {
            Log.warn(ProductAnalyticsTag, 'Cannot track ' + message + ' since sectionOrder is invalid.');
        }
        else if (!ProductAnalytics.checkCoordinate(column)) {
            Log.warn(ProductAnalyticsTag, 'Cannot track ' + message + ' since column is invalid.');
        }
        else if (!ProductAnalytics.checkCoordinate(row)) {
            Log.warn(ProductAnalyticsTag, 'Cannot track ' + message + ' since row is invalid.');
        }
        else if (!ProductAnalytics.checkContentLink(contentLink)) {
            Log.warn(ProductAnalyticsTag, 'Cannot track ' + message + ' since content link is invalid.');
        }
        else {
            valid = true;
        }
        return valid;
    }
    /**
     * Check coordinate
     * @param coordinate
     * @returns True if the coordinate is valid, false otherwise.
     */
    static checkCoordinate(coordinate) {
        return typeof coordinate === 'number' && coordinate >= 0;
    }
    /**
     * Check content link
     * @param contentLink
     * @returns True if the content link is valid, false otherwise.
     */
    static checkContentLink(contentLink) {
        return typeof contentLink === 'string' && contentLink !== '';
    }
    /**
     * Check content link type
     * @param contentLinkType
     * @returns True if the content link type is valid, false otherwise.
     */
    static checkContentLinkType(contentLinkType) {
        return typeof contentLinkType === 'string' && contentLinkType !== '';
    }
    /**
     * Check content ID
     * @param contentId The content identifier. It is used for internal content unequivocally identification (i.e., AAA000111222).
     * @returns True if the content ID is valid, false otherwise.
     */
    static checkContentId(contentId) {
        return (typeof contentId === 'string' && contentId !== '') || typeof contentId === 'number';
    }
    /**
     * Check EPG ID
     * @param epgId The content identifier. It is used for internal content unequivocally identification (i.e., AAA000111222).
     * @returns True if the EPG ID is valid, false otherwise.
     */
    static checkEpgId(epgId) {
        return (typeof epgId === 'string' && epgId !== '') || typeof epgId === 'number';
    }
    // -----------------------------------------------------------------------------------------------
    // SESSION LISTENERS
    // -----------------------------------------------------------------------------------------------
    // Listeners
    onRequestQueued(sessionRequest) {
        // Handle when needed
    }
    onRequestWillSend(sessionRequest) {
        // Handle when needed
    }
    /**
     * Request sent handler
     */
    onRequestSent(sessionRequest) {
        switch (sessionRequest.getService()) {
            case Constants.Service.SESSION_START:
            case Constants.Service.SESSION_STOP:
                this.fetchExperimentVariants();
                break;
        }
    }
}
ProductAnalytics.VIDEOKEY_DEFAULT_IDENTIFIER = 'default';
